import {getISOTimeStringForReview, getFormattedDate} from '@/utils/commons';
import {
  TARGET_TYPES,
  TARGET_TYPES_OPTIONS,
  IMAGETYPE,
  BOOLEAN_VALUES
} from '@/utils/constants/saleTargetConstants';
import {Constants} from '@/utils/constants/commonConstants';

const toValidDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const getSalesTargetActiveWindowDates = (salesTarget) => {
  const start =
    toValidDate(salesTarget?.formData?.startDate) ||
    toValidDate(salesTarget?.startDate) ||
    toValidDate(salesTarget?.activeStartDate);

  const end =
    toValidDate(salesTarget?.formData?.endDate) ||
    toValidDate(salesTarget?.endDate) ||
    toValidDate(salesTarget?.activeEndDate);

  return {start, end};
};

export const isNowWithinDateWindow = (start, end) => {
  if (!start || !end) return false;
  const now = new Date();
  return now > start && now < end;
};

/**
 * Checks if two media objects have invalid image combination
 * @param {Object} media1 - First media object
 * @param {Object} media2 - Second media object
 * @returns {boolean} - True if invalid combination exists
 */
export const hasInvalidImageCombination = (media1, media2) => {
  const hasSameImageType = (m1, m2) => {
    return (
      (m1?.imageId === IMAGETYPE.Foreground &&
        m2?.imageId === IMAGETYPE.Foreground) ||
      (m1?.imageId === IMAGETYPE.Background &&
        m2?.imageId === IMAGETYPE.Background)
    );
  };

  if (hasSameImageType(media1, media2)) return true;
  if (
    media1?.imageId == '' &&
    media1?.imageUri !== '' &&
    media2?.imageId == '' &&
    media2?.imageUri !== ''
  )
    return true;
  return false;
};

/**
 * Build a consistent payload for saving Sales Target drafts from different pages
 * without changing existing business logic.
 */
export const buildSalesTargetDraftPayload = ({
  salesTarget,
  isPrerequisiteTarget,
  fallbackSelectedType
}) => {
  const hasParentFile =
    Array.isArray(salesTarget?.tabWiseData?.parent?.mediaFile) &&
    salesTarget.tabWiseData.parent.mediaFile.length === 1;
  const hasChildFile =
    Array.isArray(salesTarget?.tabWiseData?.child?.mediaFile) &&
    salesTarget.tabWiseData.child.mediaFile.length === 1;

  const shouldUseUploadedProducts = isPrerequisiteTarget
    ? hasParentFile || hasChildFile
    : hasParentFile;

  const selectedTypeForDraft = shouldUseUploadedProducts
    ? Constants.UploadedProducts
    : fallbackSelectedType || Constants.CategoryProducts;

  return {
    salesTargetForDraft: {
      ...salesTarget,
      selectedType: selectedTypeForDraft
    },
    selectedTypeForDraft
  };
};

// Helper function to transform media array to include id key
export const transformMediaArray = (mediaArray) => {
  return mediaArray.map((mediaItem) => {
    let mappedId = mediaItem.imageId || mediaItem.id;

    // Map display text to actual values if needed
    if (mappedId === 'Foreground') {
      mappedId = IMAGETYPE.Foreground;
    } else if (mappedId === 'Background') {
      mappedId = IMAGETYPE.Background;
    }

    return {
      ...mediaItem,
      id: mappedId
    };
  });
};

export const getValidMediaData = (salesTargetData) => {
  return transformMediaArray([
    ...(salesTargetData?.displayContentMedia
      ? [salesTargetData.displayContentMedia]
      : []),
    ...(salesTargetData?.displayContentMediaOptional
      ? [salesTargetData.displayContentMediaOptional]
      : [])
  ]).filter((item) => item.imageUri); // Remove objects where imageUri is not present
};

/**
 * Creates a sales target request object based on the target type and data
 * @param {Object} salesTargetData - The sales target data from the store
 * @param {boolean} isPrerequisiteTarget - Whether this is a prerequisite target
 * @returns {Object} The formatted request object
 */

const isMainTargetOrProdTarget = (salesTargetData) => {
  return (
    salesTargetData?.targetType === TARGET_TYPES.MainTarget ||
    salesTargetData?.targetType === TARGET_TYPES.ProductTarget
  );
};

const isLayeredTarget = (salesTargetData) => {
  return (
    salesTargetData?.targetType === TARGET_TYPES.MonthlyTargetLayered ||
    salesTargetData?.targetType === TARGET_TYPES.ProductTargetLayered ||
    TARGET_TYPES_OPTIONS[salesTargetData?.targetType] ===
      TARGET_TYPES.MonthlyTargetLayered ||
    TARGET_TYPES_OPTIONS[salesTargetData?.targetType] ===
      TARGET_TYPES.ProductTargetLayered
  );
};

const getCommonProps = (salesTargetData) => {
  const commonProperties = {
    targetName: salesTargetData?.targetName || '',
    displayContent: salesTargetData?.displayContent || {},
    targetType: salesTargetData?.targetTypeValue || '',
    joinCampaignFlag: salesTargetData?.joinCampaignFlag,
    VATIncluded: !salesTargetData?.excludeTaxFromGoal,
    ...getRoundPointsValue(salesTargetData),
    offlineOrdersIncluded: salesTargetData?.offlineOrdersIncluded ?? false,
    cepRefId: salesTargetData?.cepRefId ?? null,
    displayStartDate: getISOTimeStringForReview(
      salesTargetData?.displayStartDate,
      false,
      true
    ),
    displayEndDate: getISOTimeStringForReview(
      salesTargetData?.displayEndDate,
      true,
      false
    ),
    activeStartDate: getISOTimeStringForReview(
      salesTargetData?.startDate,
      false,
      true
    ),
    activeEndDate: getISOTimeStringForReview(
      salesTargetData?.endDate,
      true,
      false
    ),
    targetDeliveryDate: getISOTimeStringForReview(
      salesTargetData?.targetDeliveryDate,
      true,
      false
    ),
    ...setPointsDeliveryDate(salesTargetData),
    media: getValidMediaData(salesTargetData),
    activeDisplay: salesTargetData?.activeDisplay ?? false,
    storeList: salesTargetData?.storeList || '',
    uuid: salesTargetData?.uuid || null,
    status: salesTargetData?.status,
    linkedSalesTargetInfo: salesTargetData?.linkedSalesTargets || [],
    metadata: {
      productFileName:
        salesTargetData?.selectedType === Constants.UploadedProducts
          ? salesTargetData?.tabWiseData?.parent?.mediaFile[0]
          : null,
      // Parent upload flag should depend on parent mediaFile, not just selectedType
      isProductIdsUploaded:
        Array.isArray(salesTargetData?.tabWiseData?.parent?.mediaFile) &&
        salesTargetData.tabWiseData.parent.mediaFile.length === 1,
      numberLevel: isLayeredTarget ? salesTargetData?.numberLevel : null,
      numberMileStone: isLayeredTarget ? salesTargetData?.numberMileStone : null
    }
  };
  return commonProperties;
};

const displayContentData = (salesTargetData) => {
  return (
    Object?.fromEntries(
      Object?.entries(salesTargetData?.displayContent || {})?.filter(
        ([key]) => key !== 'child'
      )
    ) || {}
  );
};

const getPrereqObject = (commonProperties, salesTargetData) => {
  const displayContent = displayContentData(salesTargetData);
  return {
    ...commonProperties,
    displayContent: displayContent,
    ruleSet: salesTargetData?.tabWiseData?.parent?.ruleSet,
    awardPointsImmedFlag: salesTargetData?.awardPointsImmedFlag ?? false,
    childRuleSet: null,
    ignoreSkuList: salesTargetData?.tabWiseData?.child?.ignoreSkuList ?? false,
    pointDisplay: null,
    child: {
      id: '',
      storeList: '',
      targetName: salesTargetData?.targetName || '',
      ruleSet: salesTargetData?.tabWiseData?.child?.ruleSet,
      displayContent: salesTargetData?.displayContent?.child || {},
      targetType: salesTargetData?.targetTypeValue || '',
      awardPointsImmedFlag: salesTargetData?.awardPointsImmedFlag ?? false,
      joinCampaignFlag: true,
      VATIncluded: true,
      cepRefId: salesTargetData?.cepRefId ?? null,
      childRuleSet: null,
      roundPointsValue: salesTargetData?.roundPointsValue || '',
      roundUpPointsFlag: salesTargetData?.roundUpPointsFlag ?? false,
      activeStartDate: getISOTimeStringForReview(
        salesTargetData?.startDate,
        false,
        true
      ),
      activeEndDate: getISOTimeStringForReview(
        salesTargetData?.endDate,
        true,
        false
      ),
      targetDeliveryDate: getISOTimeStringForReview(
        salesTargetData?.targetDeliveryDate,
        true,
        false
      ),
      displayStartDate: getISOTimeStringForReview(
        salesTargetData?.displayStartDate,
        false,
        true
      ),
      displayEndDate: getISOTimeStringForReview(
        salesTargetData?.displayEndDate,
        true,
        false
      ),
      media: transformMediaArray(salesTargetData?.media || []),
      status: salesTargetData?.status,
      ignoreSkuList:
        salesTargetData?.tabWiseData?.parent?.ignoreSkuList ?? false,
      offlineOrdersIncluded: salesTargetData?.offlineOrdersIncluded ?? false,
      pointDisplay: null,
      children: [],
      application: {
        createdAt: '',
        updatedAt: '',
        createdBy: null,
        updatedBy: null,
        id: '',
        country: '',
        siteId: '',
        timezone: '',
        pointsMultiplierRuleSet: {
          rules: [
            {
              name: 'Order Channel',
              matchWith: ['online'],
              multiplier: 2.0
            },
            {
              name: 'Order Channel',
              matchWith: ['offline'],
              multiplier: 1.5
            }
          ]
        },
        config: null
      },
      parent: null,
      totalTasks: null,
      metadata: {
        productFileName:
          salesTargetData?.selectedType === Constants.UploadedProducts
            ? salesTargetData?.tabWiseData?.child?.mediaFile[0]
            : null,
        // Child upload flag should depend on child mediaFile only
        isProductIdsUploaded:
          Array.isArray(salesTargetData?.tabWiseData?.child?.mediaFile) &&
          salesTargetData.tabWiseData.child.mediaFile.length === 1
      }
    },
    application: null,
    parent: null,
    totalTasks: null
  };
};

const isLinkedTargetType = (salesTargetData) => {
  return (
    salesTargetData?.targetType === TARGET_TYPES.EarlyPaymentTarget ||
    TARGET_TYPES_OPTIONS[salesTargetData?.targetType] ===
      TARGET_TYPES.EarlyPaymentTarget ||
    salesTargetData?.targetType === TARGET_TYPES.DropSizeTarget ||
    TARGET_TYPES_OPTIONS[salesTargetData?.targetType] ===
      TARGET_TYPES.DropSizeTarget
  );
};

const getEarlyPaymentObject = (commonProperties, salesTargetData) => {
  const isDropSize =
    salesTargetData?.targetType === TARGET_TYPES.DropSizeTarget ||
    TARGET_TYPES_OPTIONS[salesTargetData?.targetType] ===
      TARGET_TYPES.DropSizeTarget;
  return {
    ...commonProperties,
    // Drop Size targets always need pointsDeliveryDate, so awardPointsImmedFlag must be false
    // Early Payment: true when no linked targets (points awarded immediately), false when linked
    awardPointsImmedFlag: isDropSize
      ? false
      : commonProperties.linkedSalesTargetInfo?.length === 0,
    ignoreSkuList: true,
    pointDisplay: null,
    ruleSet: null,
    childRuleSet: null
  };
};

export const createSalesTargetRequestObject = (
  salesTargetData,
  isPrerequisiteTarget
) => {
  // Common properties shared between both types
  const commonProperties = getCommonProps(salesTargetData);

  // Early Payment Target and Drop Size Target — simplified payload
  if (isLinkedTargetType(salesTargetData)) {
    return getEarlyPaymentObject(commonProperties, salesTargetData);
  }

  // If it's not a prerequisite target, return with specific properties
  if (!isPrerequisiteTarget) {
    return {
      ...commonProperties,
      ignoreSkuList: salesTargetData?.tabWiseData?.parent?.ignoreSkuList,
      pointDisplay: salesTargetData?.pointDisplay,
      ruleSet: salesTargetData?.tabWiseData?.parent?.ruleSet,
      childRuleSet: salesTargetData?.childRuleSet,
      awardPointsImmedFlag: isMainTargetOrProdTarget(salesTargetData)
        ? salesTargetData?.awardPointsImmedFlag
        : null
    };
  }
  return getPrereqObject(commonProperties, salesTargetData);
};

const setPointsDeliveryDate = ({awardPointsImmedFlag, pointsDeliveryDate}) => ({
  pointsDeliveryDate: getISOTimeStringForReview(
    awardPointsImmedFlag ? null : pointsDeliveryDate,
    true,
    false
  )
});

const getRoundPointsValue = (salesTargetData) => {
  return {
    roundPointsValue:
      salesTargetData?.roundPointsValue || salesTargetData?.roundOffValue || '',
    roundUpPointsFlag: salesTargetData?.roundUpPointsFlag ?? false
  };
};
export const tranformRuleSetOfSalesTarget = (
  salesTargetData,
  categoryListData
) => {
  if (salesTargetData.ruleSet) {
    salesTargetData.ruleSet = transformCategoriesToLabels(
      salesTargetData.ruleSet,
      categoryListData
    );
  }
  if (salesTargetData.childRuleSet) {
    salesTargetData.childRuleSet = transformCategoriesToLabels(
      salesTargetData.childRuleSet,
      categoryListData
    );
  }
  if (salesTargetData?.child?.ruleSet) {
    salesTargetData.child.ruleSet = transformCategoriesToLabels(
      salesTargetData.child.ruleSet,
      categoryListData
    );
  }
  return salesTargetData;
};

const transformCategoriesToLabels = (ruleSet, categoryListData) => {
  if (!ruleSet?.rules?.[0]?.includedCategories) {
    return ruleSet;
  }
  const includedCategoryIds = ruleSet.rules[0].includedCategories;
  const excludedCategoryIds = ruleSet.rules[0].excludedCategories || [];
  const includedCategoriesLabels = categoryListData
    .filter(
      (cat) =>
        includedCategoryIds.includes(cat.categoryId) ||
        includedCategoryIds.includes(cat.category)
    )
    .map((cat) => cat.category);
  const excludedCategoriesLabels = categoryListData
    .filter(
      (cat) =>
        excludedCategoryIds.includes(cat.categoryId) ||
        excludedCategoryIds.includes(cat.category)
    )
    .map((cat) => cat.category);
  const ruleSet1 = {
    ...ruleSet,
    rules: [
      {
        ...ruleSet.rules[0],
        includedCategories: includedCategoriesLabels,
        excludedCategories: excludedCategoriesLabels
      }
    ]
  };
  return ruleSet1;
};

export const getSalesTargetDate = (salesTargetData, urlStatus) => {
  const isSavedRecord =
    salesTargetData?.id != null || salesTargetData?.uuid != null;

  if (urlStatus === Constants.Completed) {
    const endDate =
      salesTargetData?.formData?.activeEndDate ??
      salesTargetData?.formData?.endDate;

    return endDate
      ? 'Completed: ' + getFormattedDate(endDate, false, '-', 'yyyy-mm-dd')
      : null;
  } else if (
    urlStatus !== Constants.Completed &&
    isSavedRecord === BOOLEAN_VALUES.TRUE
  ) {
    const updatedDate = salesTargetData?.formData?.updatedAt;

    return updatedDate
      ? 'Last Updated: ' +
          getFormattedDate(updatedDate, false, '-', 'yyyy-mm-dd')
      : null;
  } else {
    return null;
  }
};
